<template>
  <UModal
    v-model:open="modalOpen"
    :dismissible="false"
    :ui="{
      content:
        'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-2rem)] max-w-5xl max-h-[calc(100dvh-2rem)] rounded-xl shadow-lg ring ring-white/10 overflow-hidden',
    }"
  >
    <template #content>
      <UButton
        color="neutral"
        variant="ghost"
        icon="i-lucide-x"
        aria-label="Close modal"
        class="absolute top-4 right-4 z-10"
        @click="modalOpen = false"
      />

      <div
        class="p-4 sm:p-6 flex flex-col h-full max-h-[calc(100dvh-2rem)] overflow-hidden"
      >
        <div class="flex items-start justify-between gap-4 shrink-0">
          <div>
            <div class="text-lg font-semibold text-white">Deleted Tasks</div>
            <div class="text-sm text-white/70 mt-1">
              Tasks in this list will be permanently deleted in 7 days.
            </div>
          </div>
        </div>

        <div
          class="mt-5 flex flex-wrap items-center justify-between gap-3 shrink-0"
        >
          <div class="flex items-center gap-3">
            <UCheckbox
              :model-value="allSelected"
              :indeterminate="isIndeterminate"
              :disabled="!rows.length || loading || restoring"
              @update:model-value="toggleAll"
            />
            <div class="text-sm text-white/70">
              Selected {{ selectedCount }}
            </div>
          </div>

          <div class="flex items-center gap-2">
            <UButton
              size="sm"
              color="neutral"
              variant="ghost"
              icon="i-lucide-refresh-cw"
              aria-label="Refresh deleted tasks"
              :loading="loading"
              :disabled="restoring"
              @click="loadDeleted"
            />

            <UButton
              size="sm"
              color="primary"
              variant="soft"
              icon="i-lucide-rotate-ccw"
              class="text-white"
              :disabled="!selectedCount || loading || restoring"
              :loading="restoring"
              @click="restoreSelected"
            >
              Restore Selected
            </UButton>
          </div>
        </div>

        <div
          class="mt-3 flex-1 min-h-0 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800/50"
        >
          <div v-if="loading" class="flex justify-center items-center py-16">
            <div
              class="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500"
            ></div>
          </div>

          <UEmpty
            v-else-if="rows.length === 0"
            icon="i-lucide-trash-2"
            title="No deleted tasks"
            description="Nothing here yet."
            :ui="{ root: 'ring-0', label: 'text-white' }"
          />

          <UCard
            v-else
            class="bg-white/5 ring-1 ring-white/10 overflow-hidden"
            :ui="{ body: 'p-0' }"
          >
            <UTable :data="pagedRows" :columns="columns" :ui="tableUi">
              <template #select-cell="{ row }">
                <UCheckbox
                  :model-value="!!selected[row.original.id]"
                  :disabled="loading || restoring"
                  @update:model-value="(v) => toggleOne(row.original.id, v)"
                />
              </template>

              <template #tool-cell="{ row }">
                <div class="flex items-center gap-3 min-w-0">
                  <!-- 视频预览 -->
                  <UPopover
                    v-if="row.original.outPutVideoUrls?.length > 0"
                    mode="hover"
                    arrow
                    :open-delay="150"
                    :close-delay="150"
                  >
                    <div
                      class="cursor-pointer hover:opacity-80 transition-opacity relative"
                    >
                      <div
                        class="relative w-10 h-10 rounded overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm bg-gray-100 dark:bg-gray-800 flex items-center justify-center"
                      >
                        <ViewportVideo
                          :src="row.original.outPutVideoUrls[0]"
                          video-class="absolute inset-0 w-full h-full object-cover z-10"
                          :muted="true"
                          :playsinline="true"
                          :preload="'metadata'"
                          :disable-picture-in-picture="true"
                          :key="`video-thumbnail-${row.original.id}`"
                          :root-margin="'200px'"
                        >
                          <template #placeholder>
                            <UIcon
                              name="i-lucide-loader-2"
                              class="w-3 h-3 animate-spin text-gray-400"
                            />
                          </template>
                        </ViewportVideo>
                        <div
                          class="absolute inset-0 items-center justify-center bg-black/10 z-20 pointer-events-none flex"
                        >
                          <UIcon
                            name="i-lucide-play"
                            class="w-2.5 h-2.5 text-white drop-shadow-sm"
                          />
                        </div>
                      </div>
                    </div>
                    <template #content>
                      <div class="p-2">
                        <div
                          class="flex flex-col gap-2 overflow-y-auto h-80 w-64 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100"
                        >
                          <ViewportVideo
                            v-for="(url, index) in row.original.outPutVideoUrls"
                            :key="`video-preview-${row.original.id}-${index}`"
                            :src="url"
                            video-class="object-contain w-full h-full"
                            :autoplay="false"
                            :muted="true"
                            :playsinline="true"
                            :controls="true"
                          />
                        </div>
                      </div>
                    </template>
                  </UPopover>

                  <!-- 图片预览 -->
                  <UPopover
                    v-else-if="row.original.outPutImageUrls?.length > 0"
                    mode="hover"
                    arrow
                    :open-delay="150"
                    :close-delay="150"
                  >
                    <div
                      class="relative w-10 h-10 rounded overflow-hidden border border-white/10 bg-gray-800 shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                    >
                      <ViewportImage
                        :src="row.original.outPutImageUrls[0]"
                        alt="output preview"
                        img-class="w-full h-full object-cover"
                        :root-margin="'100px'"
                      >
                        <template #placeholder>
                          <div class="w-full h-full bg-gray-800" />
                        </template>
                        <template #loading>
                          <div
                            class="flex justify-center items-center w-full h-full"
                          >
                            <UIcon
                              name="i-lucide-loader-2"
                              class="w-4 h-4 animate-spin text-white/40"
                            />
                          </div>
                        </template>
                      </ViewportImage>
                    </div>
                    <template #content>
                      <div class="p-2">
                        <div
                          class="flex flex-col gap-4 overflow-y-auto max-h-80 min-h-20 w-64 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100 items-center custom-scroll"
                        >
                          <div
                            v-for="(url, index) in row.original.outPutImageUrls"
                            :key="`output-image-${row.original.id}-${index}`"
                            class="relative"
                          >
                            <img
                              :ref="
                                (el) => {
                                  if (el) {
                                    // 检查是否已有缓存的 blob URL
                                    if (imageBlobCache.has(url)) {
                                      el.src = imageBlobCache.get(url);
                                      imageLoadedStates[url] = true;
                                    } else {
                                      // 检查当前元素是否已加载
                                      nextTick(() => {
                                        checkImageCache(url, el);
                                      });
                                    }
                                  }
                                }
                              "
                              :src="
                                imageBlobCache.has(url)
                                  ? imageBlobCache.get(url)
                                  : url
                              "
                              :alt="`预览 ${index + 1}`"
                              class="w-auto h-auto rounded-md shadow-md object-contain"
                              :class="{
                                'opacity-0': !imageLoadedStates[url],
                              }"
                              @load="handleImageLoad(url, $event)"
                              @error="handleImageLoad(url, $event)"
                            />
                            <div
                              v-show="!imageLoadedStates[url]"
                              class="absolute inset-0 flex justify-center items-center pointer-events-none"
                            >
                              <div
                                class="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                  </UPopover>

                  <!-- 无预览内容占位符 -->
                  <div
                    v-else
                    class="relative w-10 h-10 rounded overflow-hidden border border-white/10 bg-gray-800 shrink-0"
                  >
                    <div class="w-full h-full flex items-center justify-center">
                      <UIcon
                        name="i-lucide-image-off"
                        class="w-4 h-4 text-white/30"
                      />
                    </div>
                  </div>

                  <div class="min-w-0">
                    <div class="text-white font-medium whitespace-nowrap">
                      {{ toolNameMap[row.original.toolName] || "-" }}
                    </div>
                  </div>
                </div>
              </template>

              <template #prompt-cell="{ row }">
                <div
                  class="text-white/80 max-w-[120px] sm:max-w-[320px] truncate"
                  :title="row.original.prompt || ''"
                >
                  {{ row.original.prompt || "-" }}
                </div>
              </template>

              <!-- <template #deleted_at-cell="{ row }">
                <div class="text-white/80 whitespace-nowrap">
                  {{
                    row.original.deletedAt
                      ? formatTime(row.original.deletedAt)
                      : "-"
                  }}
                </div>
              </template> -->

              <template #purge_in-cell="{ row }">
                <div class="text-white/80 whitespace-nowrap">
                  <template v-if="row.original.deletedAt">
                    {{ purgeDays(row.original.deletedAt) }} days left
                  </template>
                  <template v-else>-</template>
                </div>
              </template>

              <template #actions-cell="{ row }">
                <UButton
                  size="sm"
                  color="primary"
                  variant="ghost"
                  icon="i-lucide-rotate-ccw"
                  :loading="isRestoring(row.original.id)"
                  :disabled="loading || restoring"
                  @click="restoreIds([row.original.id])"
                >
                  Restore
                </UButton>
              </template>
            </UTable>
          </UCard>
        </div>

        <!-- Pagination -->
        <div
          class="mt-4 flex justify-center shrink-0"
          v-if="rows && rows.length > 0 && rows.length / itemsPerPage > 1"
        >
          <UPagination
            v-model:page="page"
            :total="rows.length"
            :items-per-page="itemsPerPage"
            color="primary"
            variant="subtle"
            :sibling-count="1"
            @update:page="onPageChange"
          />
        </div>
      </div>
    </template>
  </UModal>
</template>

<script setup>
import { computed, ref, watch, reactive, nextTick } from "vue";
import { useStore } from "vuex";
import ViewportImage from "~/components/viewport/ViewportImage.vue";
import ViewportVideo from "~/components/viewport/ViewportVideo.vue";
import { resolveUserR2Url } from "@/utils/r2Url";

const props = defineProps({
  open: { type: Boolean, default: false },
  userId: { type: [String, Number], default: "" },
  menuData: { type: Array, default: () => [] },
});

const emit = defineEmits(["update:open", "restored"]);

const store = useStore();
const toast = useToast();

const loading = ref(false);
const restoring = ref(false);
const restoringIds = ref({});
const deletedTasks = ref([]);
const selected = ref({});

// 分页相关
const page = ref(1);
const itemsPerPage = ref(10);
const pagedRows = computed(() => {
  const start = (page.value - 1) * itemsPerPage.value;
  return (rows.value || []).slice(start, start + itemsPerPage.value);
});

const modalOpen = computed({
  get: () => props.open,
  set: (v) => emit("update:open", v),
});

const rows = computed(() =>
  (deletedTasks.value || []).map((task) => ({
    id: task.id,
    toolName: task.toolName,
    model: task.model,
    outPutImageUrls: task.outPutImageUrls,
    outPutVideoUrls: task.outPutVideoUrls,
    prompt: task.prompt,
    deletedAt: task.deletedAt,
  }))
);

const columns = [
  { accessorKey: "select", header: "" },
  { accessorKey: "tool", header: "Tool" },
  { accessorKey: "prompt", header: "Prompt" },
  //{ accessorKey: "deleted_at", header: "Deleted At" },
  { accessorKey: "purge_in", header: "Auto-delete in" },
  { accessorKey: "actions", header: "" },
];

const toolNameMap = computed(() => {
  return (props.menuData || []).reduce((acc, group) => {
    for (const item of Array.isArray(group?.children) ? group.children : []) {
      if (item?.ref) acc[item.ref] = item.title || item.ref;
    }
    return acc;
  }, {});
});

const tableUi = {
  root: "relative overflow-auto",
  base: "min-w-full",
  thead: "bg-white/10",
  tbody: "divide-y divide-white/10",
  tr: "hover:bg-white/5",
  th: "px-4 py-3 text-xs font-semibold text-white/80 text-left",
  td: "px-4 py-3 text-sm text-white/80 align-middle",
  empty: "py-10 text-center text-sm text-white/60",
  loading: "py-10 text-center",
};

const selectedIds = computed(() =>
  Object.entries(selected.value)
    .filter(([, v]) => v)
    .map(([id]) => id)
);

// 跟踪图片加载状态，避免重复请求
const imageLoadedStates = reactive({});
// 缓存已加载图片的 blob URL，完全避免重复网络请求
const imageBlobCache = new Map();

// 将图片 URL 转换为 blob URL 并缓存
const getCachedImageUrl = async (url) => {
  // 如果已经有缓存的 blob URL，直接返回
  if (imageBlobCache.has(url)) {
    imageLoadedStates[url] = true;
    return imageBlobCache.get(url);
  }

  try {
    // 获取图片并转换为 blob
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    // 缓存 blob URL
    imageBlobCache.set(url, blobUrl);
    imageLoadedStates[url] = true;

    return blobUrl;
  } catch (error) {
    console.error("Failed to cache image:", error);
    // 如果失败，返回原始 URL
    imageLoadedStates[url] = true;
    return url;
  }
};

// 处理图片加载
const handleImageLoad = (url, event) => {
  imageLoadedStates[url] = true;
  // 如果还没有缓存，尝试缓存
  if (!imageBlobCache.has(url) && event.target?.src) {
    getCachedImageUrl(url).catch(() => {
      // 静默失败，使用原始 URL
    });
  }
};

// 检查图片是否已经在浏览器缓存中
const checkImageCache = (url, imgElement) => {
  if (imgElement && imgElement.complete && imgElement.naturalHeight !== 0) {
    imageLoadedStates[url] = true;
    // 尝试缓存图片
    getCachedImageUrl(url).catch(() => {
      // 静默失败
    });
    return true;
  }
  return false;
};

const selectedCount = computed(() => selectedIds.value.length);

const allSelected = computed(() => {
  if (!rows.value.length) return false;
  return rows.value.every((r) => !!selected.value[r.id]);
});

const isIndeterminate = computed(() => {
  if (!rows.value.length) return false;
  const anySelected = rows.value.some((r) => !!selected.value[r.id]);
  return anySelected && !allSelected.value;
});

function toggleAll(val) {
  const next = {};
  for (const row of rows.value) next[row.id] = val;
  selected.value = next;
}

function toggleOne(id, val) {
  selected.value = { ...selected.value, [id]: val };
}

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) loadDeleted();
  }
);

watch(
  () => props.userId,
  (newId, oldId) => {
    if (props.open && newId && newId !== oldId) loadDeleted();
  }
);

function parseMaybeJson(value, fallback) {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  }
  if (typeof value === "object") return value;
  return fallback;
}

function parseApiTime(value) {
  if (!value) return null;
  const normalized = /\d{2}:\d{2}:\d{2}$/.test(value) ? value + "Z" : value;
  const t = new Date(normalized).getTime();
  return Number.isNaN(t) ? null : t;
}

function extractOutputUrls(output) {
  if (output === null || output === undefined || output === "") return [];
  if (Array.isArray(output)) {
    return output
      .filter((v) => v !== null && v !== undefined)
      .map((v) => String(v))
      .map(resolveUserR2Url)
      .filter(Boolean);
  }
  if (typeof output === "string") {
    try {
      const parsed = JSON.parse(output);
      return extractOutputUrls(parsed);
    } catch {
      return output ? [resolveUserR2Url(output)] : [];
    }
  }
  if (typeof output === "object") {
    if (Array.isArray(output.urls)) return extractOutputUrls(output.urls);
    if (typeof output.url === "string") return [resolveUserR2Url(output.url)];
  }
  return [];
}

function isProbablyImageUrl(url) {
  if (!url) return false;
  if (url.startsWith("data:image/")) return true;
  return /\.(png|jpe?g|webp|gif|avif|bmp|svg)(\?|#|$)/i.test(url);
}

function isProbablyVideoUrl(url) {
  if (!url) return false;
  if (url.startsWith("data:video/")) return true;
  return /\.(mp4|webm|ogg|mov|avi|wmv|flv|mkv|m4v)(\?|#|$)/i.test(url);
}

function extractPrompt(input) {
  const obj = parseMaybeJson(input, {});
  if (!obj || typeof obj !== "object") return "";
  return (
    obj.prompt ||
    obj.positive_prompt ||
    obj.text ||
    obj.query ||
    obj.description ||
    obj.caption ||
    ""
  );
}

function formatTime(timestamp) {
  const userLocale = navigator.language || "en-US";
  const date = new Date(timestamp);
  const formatter = new Intl.DateTimeFormat(userLocale, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  return formatter.format(date);
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;
function purgeDays(deleteAtMs) {
  if (!deleteAtMs) return 0;
  const deadline = deleteAtMs + 7 * MS_PER_DAY;
  const diff = deadline - Date.now();
  return Math.max(0, Math.ceil(diff / MS_PER_DAY));
}

async function loadDeleted() {
  if (!props.userId) return;
  if (loading.value) return;

  loading.value = true;
  try {
    const res = await store.dispatch("ai/getUserHistory", {
      userId: props.userId,
      get_type: "deleted_only",
    });

    const data = Array.isArray(res?.data)
      ? res.data
      : Array.isArray(res?.data?.items)
      ? res.data.items
      : [];
    deletedTasks.value = data.map((item) => {
      const id =
        item?.id !== undefined && item?.id !== null ? String(item.id) : "";
      const outputUrls = extractOutputUrls(item?.output);
      const outPutImageUrls = outputUrls.filter(isProbablyImageUrl);
      const outPutVideoUrls = outputUrls.filter(isProbablyVideoUrl);
      return {
        id,
        toolName: item.tool_name || item.toolName || "",
        model: item.model || "",
        outPutImageUrls,
        outPutVideoUrls,
        prompt: extractPrompt(item.input),
        deletedAt: parseApiTime(item.delete_at),
      };
    });

    // Keep selection only for existing rows
    const keep = {};
    for (const row of rows.value) {
      if (selected.value[row.id]) keep[row.id] = true;
    }
    selected.value = keep;
    // 重置页码
    page.value = 1;
  } catch (error) {
    console.error("Failed to load deleted tasks:", error);
    toast.add({
      title: "Failed to load deleted tasks",
      color: "error",
      icon: "i-heroicons-exclamation-triangle",
    });
  } finally {
    loading.value = false;
  }
}

function onPageChange(newPage) {
  page.value = newPage;
}

function isRestoring(id) {
  return !!restoringIds.value[id];
}

async function restoreSelected() {
  await restoreIds(selectedIds.value);
}

async function restoreIds(ids) {
  const uniqueIds = Array.from(new Set(ids)).filter(Boolean);
  if (!uniqueIds.length) return;

  restoring.value = true;
  const successIds = [];
  const failedIds = [];

  try {
    for (const id of uniqueIds) {
      restoringIds.value[id] = true;
      try {
        await store.dispatch("ai/setUserHistory", { id, set_type: "restore" });
        successIds.push(id);
      } catch (err) {
        console.error("Failed to restore task:", id, err);
        failedIds.push(id);
      } finally {
        restoringIds.value[id] = false;
      }
    }

    if (successIds.length) {
      deletedTasks.value = deletedTasks.value.filter(
        (t) => !successIds.includes(t.id)
      );
      const nextSelected = { ...selected.value };
      for (const id of successIds) delete nextSelected[id];
      selected.value = nextSelected;
      emit("restored");
    }

    if (!failedIds.length) {
      toast.add({
        title: "Task restored successfully",
        color: "success",
        icon: "i-heroicons-check-circle",
      });
    } else {
      toast.add({
        title: `Restored ${successIds.length}, failed ${failedIds.length}`,
        color: "error",
        icon: "i-heroicons-exclamation-triangle",
      });
    }
  } finally {
    restoring.value = false;
  }
}
</script>
